import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  apiService,
  LuckPermsGroupDTO,
  PlayerGroupsResponse,
  type RequestOptions,
} from '@/services/api.service';
import { useAuthStore } from '@/stores/auth.store';
import { useActiveGameGroup } from '@/stores/gameGroups.store';
import { onGameGroup, type GameGroupLabel } from '@/utils/gameGroups';
import { isProtectedGrade } from '@/pages/gradesByGameGroup';
import styles from './RankManager.module.scss';

interface RankManagerProps {
  playerUuid: string;
  playerName: string;
  onRankChange?: () => void;
  /**
   * Groupe de jeu imposé à TOUTES les requêtes du cadre (option `gameGroup` du service API), quel
   * que soit le groupe actif : fiche joueur d'un groupe, grades d'un groupe « grades seuls » (un
   * lobby sans base). Titre « Grades du <groupe> », et une lecture en échec (satellite injoignable,
   * refus) est dite dans le cadre au lieu de le masquer. Omis : groupe actif, cadre d'avant.
   */
  group?: GameGroupLabel;
  /** Motif quand la passerelle refuse le groupe imposé : le cadre le dit, sans aucune requête. */
  unavailable?: string;
}

export function RankManager({ playerUuid, playerName, onRankChange, group, unavailable }: RankManagerProps) {
  const [playerGroups, setPlayerGroups] = useState<PlayerGroupsResponse | null>(null);
  const [manageableGroups, setManageableGroups] = useState<LuckPermsGroupDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Lecture en échec, affichée seulement dans un cadre à groupe imposé (le cadre du groupe actif
  // reste masqué comme avant quand aucun grade n'est gérable).
  const [loadError, setLoadError] = useState<string | null>(null);
  // Grades protégés du groupe (en minuscules), lus avec les grades gérables : le satellite refuse de
  // les attribuer à un non-admin, même s'il porte recube.allow-rank.<grade>.
  const [protectedGrades, setProtectedGrades] = useState<string[]>([]);
  const isAdmin = useAuthStore((s) => !!s.user?.isAdmin);
  // Numéro de la dernière lecture : la réponse d'une lecture dépassée (joueur précédent, groupe
  // changé) est ignorée au lieu d'écraser celle du joueur affiché.
  const loadSeq = useRef(0);
  // Groupe de jeu visé (NationsGlory : Nova, Iris), nommé dans la confirmation ; null pour Paladium.
  const activeGameGroup = useActiveGameGroup();
  const gameGroup = group ?? activeGameGroup;
  const forcedKey = group?.key ?? null;
  // Groupe imposé à chaque requête du cadre (même modèle que la page Grades) ; omis = groupe actif.
  const opts = useMemo<RequestOptions | undefined>(() => (forcedKey ? { gameGroup: forcedKey } : undefined), [forcedKey]);
  const title = group ? `Grades du ${group.label}` : 'Gestion des Grades';

  /**
   * Lit les grades du joueur et les grades gérables. `afterWrite` : relecture qui suit une écriture
   * réussie ; son échec est dit dans le bandeau d'erreur, à côté du succès, et le cadre garde la
   * liste précédente au lieu de passer en « indisponibles » (le grade est bien posé).
   */
  const loadData = useCallback(async (afterWrite = false) => {
    // Avant la branche `unavailable` : elle aussi rend obsolète une lecture en cours.
    const seq = ++loadSeq.current;
    if (unavailable) {
      setLoading(false);
      return;
    }
    setLoading(true);
    if (!afterWrite) {
      setError(null);
      setLoadError(null);
    }

    const [groupsResult, manageableResult] = await Promise.all([
      apiService.getPlayerGroups(playerUuid, opts),
      apiService.getManageableGroups(opts),
    ]);
    // Lecture dépassée (joueur précédent) : ignorée, la dernière lecture fait foi.
    if (seq !== loadSeq.current) return;

    if (groupsResult.data) {
      setPlayerGroups(groupsResult.data);
    }

    if (manageableResult.data) {
      setManageableGroups(manageableResult.data.groups);
      setProtectedGrades((manageableResult.data.protectedGrades ?? []).map((n) => n.toLowerCase()));
    }

    const readError = manageableResult.error ?? groupsResult.error ?? null;
    if (afterWrite && readError) {
      setError(`Modification faite, mais relecture impossible : ${readError}`);
    } else {
      setLoadError(readError);
    }
    setLoading(false);
  }, [playerUuid, opts, unavailable]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddGroup = async (groupName: string) => {
    setActionLoading(groupName);
    setError(null);
    setShowAddDropdown(false);

    const seqAtWrite = loadSeq.current;
    const result = await apiService.addGroupToPlayer(playerUuid, groupName, opts);
    // Joueur changé pendant l'écriture : sa lecture fait foi, rien à annoncer sur sa fiche.
    if (seqAtWrite !== loadSeq.current) {
      setActionLoading(null);
      return;
    }

    if (result.error) {
      setError(result.error);
    } else {
      setSuccessMessage(`Grade "${groupName}" ajoute`);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadData(true);
      onRankChange?.();
    }

    setActionLoading(null);
  };

  const handleRemoveGroup = async (groupName: string) => {
    if (!confirm(`Retirer le grade "${groupName}" de ${playerName}${onGameGroup(gameGroup)} ?`)) {
      return;
    }

    setActionLoading(groupName);
    setError(null);

    const seqAtWrite = loadSeq.current;
    const result = await apiService.removeGroupFromPlayer(playerUuid, groupName, opts);
    // Joueur changé pendant l'écriture : sa lecture fait foi, rien à annoncer sur sa fiche.
    if (seqAtWrite !== loadSeq.current) {
      setActionLoading(null);
      return;
    }

    if (result.error) {
      setError(result.error);
    } else {
      setSuccessMessage(`Grade "${groupName}" retire`);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadData(true);
      onRankChange?.();
    }

    setActionLoading(null);
  };

  // Grades ajoutables : gérables et pas déjà portés. Un grade protégé n'est pas proposé à un
  // non-admin, comme sur la page Grades : le satellite refuserait (403, requalifié en 502 « refus
  // d'assertion » par la passerelle, motif trompeur). Son retrait reste permis (canRemove).
  const availableToAdd = manageableGroups.filter(
    (g) => !playerGroups?.groups.includes(g.name) && (isAdmin || !isProtectedGrade(g.name, protectedGrades))
  );

  // Grades hérités, hors grades directs (satellite 1.0.10 ou plus ; sinon aucun, comme avant)
  const directLower = new Set((playerGroups?.groups ?? []).map((g) => g.toLowerCase()));
  const inherited = (playerGroups?.inheritedGroups ?? []).filter((g) => !directLower.has(g.toLowerCase()));

  // Get groups that can be removed (manageable and currently assigned)
  const canRemove = (groupName: string) => {
    if (groupName === 'default') return false;
    return manageableGroups.some((g) => g.name === groupName);
  };

  // Cadre à groupe imposé : groupe refusé par la passerelle, ou lecture en échec (satellite
  // injoignable...). Le cadre le dit, la fiche reste entière. Une lecture en échec se relance d'ici :
  // rien d'autre ne la relance tant que le joueur affiché ne change pas.
  const frameError = group
    ? (unavailable ?? (!loading && loadError ? `${title} indisponibles : ${loadError}` : null))
    : null;
  if (frameError) {
    return (
      <section className={styles.card}>
        <h3 className={styles.cardTitle}>
          <span className={styles.cardTitleIcon}>&#x1F451;</span>
          {title}
        </h3>
        <div className={`${styles.errorBanner} ${styles.errorBannerRow}`}>
          <span>{frameError}</span>
          {!unavailable && (
            <button type="button" className={styles.retryButton} onClick={() => void loadData()} disabled={loading}>
              Réessayer
            </button>
          )}
        </div>
      </section>
    );
  }

  // If staff can't manage any groups, don't show this component
  if (!loading && manageableGroups.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <section className={styles.card}>
        <h3 className={styles.cardTitle}>
          <span className={styles.cardTitleIcon}>&#x1F451;</span>
          {title}
        </h3>
        <div className={styles.loading}>
          <div className={styles.spinnerSmall} />
        </div>
      </section>
    );
  }

  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>
        <span className={styles.cardTitleIcon}>&#x1F451;</span>
        {title}
        {availableToAdd.length > 0 && (
          <div className={styles.addButtonContainer}>
            <button
              className={styles.addButton}
              onClick={() => setShowAddDropdown(!showAddDropdown)}
              disabled={actionLoading !== null}
            >
              + Ajouter
            </button>
            {showAddDropdown && (
              <div className={styles.dropdown}>
                {availableToAdd.map((group) => (
                  <button
                    key={group.name}
                    className={styles.dropdownItem}
                    onClick={() => handleAddGroup(group.name)}
                    disabled={actionLoading !== null}
                  >
                    <span className={styles.groupName}>{group.displayName || group.name}</span>
                    <span className={styles.groupId}>{group.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </h3>

      {error && <div className={styles.errorBanner}>{error}</div>}
      {successMessage && <div className={styles.successBanner}>{successMessage}</div>}

      <div className={styles.groupsList}>
        {playerGroups?.groups.map((groupName) => {
          const groupInfo = manageableGroups.find((g) => g.name === groupName);
          const isPrimary = groupName === playerGroups.primaryGroup;
          const isManageable = canRemove(groupName);

          return (
            <div
              key={groupName}
              className={`${styles.groupItem} ${isPrimary ? styles.primary : ''} ${isManageable ? styles.manageable : ''}`}
            >
              <div className={styles.groupInfo}>
                <span className={styles.groupBadge}>
                  {groupInfo?.displayName || groupName}
                </span>
                {isPrimary && <span className={styles.primaryBadge}>Principal</span>}
              </div>
              {isManageable && (
                <button
                  className={styles.removeButton}
                  onClick={() => handleRemoveGroup(groupName)}
                  disabled={actionLoading !== null}
                  title={`Retirer le grade ${groupName}`}
                >
                  {actionLoading === groupName ? (
                    <div className={styles.spinnerTiny} />
                  ) : (
                    <span>&times;</span>
                  )}
                </button>
              )}
            </div>
          );
        })}

        {/* Grades hérités (par un grade direct) : ceux que /lp info montre aussi en jeu. Jamais
            supprimables ici : on retire le grade direct qui les apporte. */}
        {inherited.map((groupName) => {
          const groupInfo = manageableGroups.find((g) => g.name === groupName);
          const isPrimary = groupName === playerGroups?.primaryGroup;
          return (
            <div
              key={`inherited-${groupName}`}
              className={`${styles.groupItem} ${styles.inherited} ${isPrimary ? styles.primary : ''}`}
              title={`${groupName} : hérité d'un autre grade du joueur. Retire le grade qui l'apporte pour le lui enlever.`}
            >
              <div className={styles.groupInfo}>
                <span className={styles.groupBadge}>{groupInfo?.displayName || groupName}</span>
                {isPrimary && <span className={styles.primaryBadge}>Principal</span>}
                <span className={styles.inheritedBadge}>hérité</span>
              </div>
            </div>
          );
        })}

        {(!playerGroups?.groups || playerGroups.groups.length === 0) && (
          <p className={styles.emptyText}>Aucun grade</p>
        )}
      </div>

      <div className={styles.hint}>
        Vous pouvez gerer {manageableGroups.length} grade{manageableGroups.length > 1 ? 's' : ''}
      </div>
    </section>
  );
}
